import React, { useState, useEffect } from "react";
import api from '../../api';

const IslandShopModal = ({ onClose, onPokemonChange }) => {
  const [activeTab, setActiveTab] = useState("berries");
  const [inventory, setInventory] = useState([]);
  const [rockSlots, setRockSlots] = useState(4); // Player starts with 4 slots
  const [rockSlotTypes, setRockSlotTypes] = useState([]); // Array of rock types per slot
  const [pokemonSlots, setPokemonSlots] = useState(3); // Player starts with 3 slots
  const [pokemonSlotSpecies, setPokemonSlotSpecies] = useState([]); // Array of Pokemon species per slot
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedRockSlot, setSelectedRockSlot] = useState(null); // For rock upgrade
  const [selectedPokemonSlot, setSelectedPokemonSlot] = useState(null); // For Pokemon change
  const [pokemonSubTab, setPokemonSubTab] = useState('change'); // 'change' or 'unlock'

  useEffect(() => {
    fetchInventory();
    fetchRockSlots();
    fetchPokemonSlots();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await api.get("/v1/inventory", { withCredentials: true });
      setInventory(response.data);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  const fetchRockSlots = async () => {
    try {
      const response = await api.get("/v1/island/rock-slots", { withCredentials: true });
      setRockSlots(response.data.slots || 4);
      setRockSlotTypes(response.data.rock_slot_types || []);
    } catch (error) {
      console.error("Error fetching rock slots:", error);
    }
  };

  const [pokemonSlotUpgradeCosts, setPokemonSlotUpgradeCosts] = useState([]);
  const [nextPokemonUpgradeCost, setNextPokemonUpgradeCost] = useState(null);

  const fetchPokemonSlots = async () => {
    try {
      const response = await api.get("/v1/island/slots", { withCredentials: true });
      setPokemonSlots(response.data.pokemon_slots || 3);
      setPokemonSlotSpecies(response.data.pokemon_slot_species || []);
      setPokemonSlotUpgradeCosts(response.data.pokemon_slot_upgrade_costs || []);
      setNextPokemonUpgradeCost(response.data.next_pokemon_upgrade_cost);
    } catch (error) {
      console.error("Error fetching pokemon slots:", error);
    }
  };

  const getItemAmount = (itemId) => {
    const item = inventory.find((i) => i.item === itemId);
    return item ? item.amount : 0;
  };

  const getOldRod = () => {
    return inventory.find((i) => i.item === 137); // Old Rod item ID
  };

  const getGoodRod = () => {
    return inventory.find((i) => i.item === 138); // Good Rod item ID
  };

  const getSuperRod = () => {
    return inventory.find((i) => i.item === 139); // Super Rod item ID
  };

  const [userLevel, setUserLevel] = useState(0);

  useEffect(() => {
    fetchUserLevel();
  }, []);

  const fetchUserLevel = async () => {
    try {
      const response = await api.get("/v1/profile", { withCredentials: true });
      // Calculate level from FISHING exp (not trainer exp) for fishing rod requirements
      const fishingExp = response.data.fishingexp || 0;
      const lvl = Math.floor(Math.pow(fishingExp, 1 / 2.5));
      const calculatedLevel = Math.pow(lvl + 1, 2.5) <= fishingExp ? lvl + 1 : lvl;
      setUserLevel(calculatedLevel);
    } catch (error) {
      console.error("Error fetching user level:", error);
    }
  };

  const handleBuyOldRod = async () => {
    const oldRod = getOldRod();
    const purchaseCost = 500; // Cost to buy a new Old Rod
    
    if (oldRod) {
      setErrorMessage("You already have an Old Rod!");
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/buy-rod",
        { rod_item_id: 137, cost: purchaseCost },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        setSuccessMessage("Old Rod purchased successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Purchase failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleRepairOldRod = async () => {
    const oldRod = getOldRod();
    const repairCost = 250;
    
    if (!oldRod) {
      setErrorMessage("You don't have an Old Rod!");
      return;
    }

    if (oldRod.durability > 0) {
      setErrorMessage("Your Old Rod doesn't need repairs!");
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/repair-rod",
        { rod_item_id: 137, cost: repairCost },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        setSuccessMessage("Old Rod repaired successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Repair failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleBuyGoodRod = async () => {
    const goodRod = getGoodRod();
    const purchaseCost = 2500; // Cost in Fisher Coins
    const fisherCoins = getItemAmount(86);
    
    if (goodRod) {
      setErrorMessage("You already have a Good Rod!");
      return;
    }

    if (userLevel < 50) {
      setErrorMessage("You need to be level 50 or higher to purchase the Good Rod!");
      return;
    }

    if (fisherCoins < purchaseCost) {
      setErrorMessage(`You need ${purchaseCost} Fisher Coins! You have ${fisherCoins}.`);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/buy-rod",
        { rod_item_id: 138, cost: purchaseCost },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        setSuccessMessage("Good Rod purchased successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Purchase failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleRepairGoodRod = async () => {
    const goodRod = getGoodRod();
    const repairCost = 1000; // Cost in Fisher Coins
    const fisherCoins = getItemAmount(86);
    
    if (!goodRod) {
      setErrorMessage("You don't have a Good Rod!");
      return;
    }

    if (goodRod.durability > 0) {
      setErrorMessage("Your Good Rod doesn't need repairs!");
      return;
    }

    if (fisherCoins < repairCost) {
      setErrorMessage(`You need ${repairCost} Fisher Coins! You have ${fisherCoins}.`);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/repair-rod",
        { rod_item_id: 138, cost: repairCost },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        setSuccessMessage("Good Rod repaired successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Repair failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleBuySuperRod = async () => {
    const superRod = getSuperRod();
    const purchaseCost = 5000; // Cost in Fisher Coins
    const fisherCoins = getItemAmount(86);
    
    if (superRod) {
      setErrorMessage("You already have a Super Rod!");
      return;
    }

    if (userLevel < 150) {
      setErrorMessage("You need to be level 150 or higher to purchase the Super Rod!");
      return;
    }

    if (fisherCoins < purchaseCost) {
      setErrorMessage(`You need ${purchaseCost} Fisher Coins! You have ${fisherCoins}.`);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/buy-rod",
        { rod_item_id: 139, cost: purchaseCost },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        setSuccessMessage("Super Rod purchased successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Purchase failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleRepairSuperRod = async () => {
    const superRod = getSuperRod();
    const repairCost = 2000; // Cost in Fisher Coins
    const fisherCoins = getItemAmount(86);
    
    if (!superRod) {
      setErrorMessage("You don't have a Super Rod!");
      return;
    }

    if (superRod.durability > 0) {
      setErrorMessage("Your Super Rod doesn't need repairs!");
      return;
    }

    if (fisherCoins < repairCost) {
      setErrorMessage(`You need ${repairCost} Fisher Coins! You have ${fisherCoins}.`);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/repair-rod",
        { rod_item_id: 139, cost: repairCost },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        setSuccessMessage("Super Rod repaired successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Repair failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleBerryConversion = async (fromItemId, fromAmount, toItemId, toAmount) => {
    const hasEnough = getItemAmount(fromItemId) >= fromAmount;
    
    if (!hasEnough) {
      setErrorMessage(`You need ${fromAmount} of this item to make this trade!`);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/convert-berry",
        {
          from_item_id: fromItemId,
          from_amount: fromAmount,
          to_item_id: toItemId,
          to_amount: toAmount,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        setErrorMessage(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Conversion failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleRockTypeUpgrade = async (slotIndex, newType, cost) => {
    const costItemId = 202; // Magost Berry
    const hasEnough = getItemAmount(costItemId) >= cost;
    
    if (!hasEnough) {
      setErrorMessage(`You need ${cost} Magost Berries to upgrade this rock!`);
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/upgrade-rock-type",
        {
          slot_index: slotIndex,
          new_type: newType,
          cost_amount: cost,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        fetchRockSlots();
        setSelectedRockSlot(null);
        setErrorMessage(null);
        setSuccessMessage(`${response.data.message} Leave and re-enter the island to see your new rock!`);
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Upgrade failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleRockSlotUpgrade = async (cost, costItemId) => {
    const hasEnough = getItemAmount(costItemId) >= cost;
    
    if (!hasEnough) {
      setErrorMessage(`You need ${cost} berries to purchase this upgrade!`);
      return;
    }

    if (rockSlots >= 18) {
      setErrorMessage("You already have the maximum number of rock slots!");
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/upgrade-rock-slots",
        {
          cost_item_id: costItemId,
          cost_amount: cost,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        fetchRockSlots();
        setErrorMessage(null);
        setSuccessMessage(`${response.data.message} Leave and re-enter the island to see your new slot!`);
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Upgrade failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handlePokemonSlotUpgrade = async () => {
    if (!nextPokemonUpgradeCost) {
      setErrorMessage("No more Pokemon slots available to unlock!");
      return;
    }

    const hasEnough = getItemAmount(nextPokemonUpgradeCost.item_id) >= nextPokemonUpgradeCost.amount;
    
    if (!hasEnough) {
      setErrorMessage(`You need ${nextPokemonUpgradeCost.amount} items to purchase this upgrade!`);
      return;
    }

    if (pokemonSlots >= 12) {
      setErrorMessage("You already have the maximum number of Pokemon slots!");
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post(
        "/v1/island/upgrade-pokemon-slots",
        { slots_to_add: 1 },
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchInventory();
        fetchPokemonSlots();
        setErrorMessage(null);
        setSuccessMessage(`Pokemon slot unlocked! You now have ${response.data.new_pokemon_slots} slots. Leave and re-enter the island to see your new slot!`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Upgrade failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const getRockSlotUpgradeCost = () => {
    if (rockSlots >= 18) return null; // Max slots reached
    
    // Individual slot costs (not pairs)
    const slotCosts = [
      { slot: 5, cost: 50, itemId: 203, itemName: "Razz Berry", image: "/items/razzberry.png" },
      { slot: 6, cost: 50, itemId: 203, itemName: "Razz Berry", image: "/items/razzberry.png" },
      { slot: 7, cost: 100, itemId: 203, itemName: "Razz Berry", image: "/items/razzberry.png" },
      { slot: 8, cost: 100, itemId: 203, itemName: "Razz Berry", image: "/items/razzberry.png" },
      { slot: 9, cost: 100, itemId: 199, itemName: "Rawst Berry", image: "/items/rawstberry.png" },
      { slot: 10, cost: 100, itemId: 199, itemName: "Rawst Berry", image: "/items/rawstberry.png" },
      { slot: 11, cost: 150, itemId: 201, itemName: "Aspear Berry", image: "/items/aspearberry.png" },
      { slot: 12, cost: 150, itemId: 201, itemName: "Aspear Berry", image: "/items/aspearberry.png" },
      { slot: 13, cost: 150, itemId: 200, itemName: "Sitrus Berry", image: "/items/sitrusberry.png" },
      { slot: 14, cost: 150, itemId: 200, itemName: "Sitrus Berry", image: "/items/sitrusberry.png" },
      { slot: 15, cost: 200, itemId: 202, itemName: "Magost Berry", image: "/items/magostberry.png" },
      { slot: 16, cost: 200, itemId: 202, itemName: "Magost Berry", image: "/items/magostberry.png" },
      { slot: 17, cost: 500, itemId: 202, itemName: "Magost Berry", image: "/items/magostberry.png" },
      { slot: 18, cost: 500, itemId: 202, itemName: "Magost Berry", image: "/items/magostberry.png" },
    ];
    
    // Find the next slot to unlock
    const nextSlotIndex = rockSlots - 4; // Current slots - starting slots
    if (nextSlotIndex >= slotCosts.length) return null;
    
    return slotCosts[nextSlotIndex];
  };

  const berryConversions = [
    { from: 227, fromName: "Rock", fromAmount: 20, fromImage: "/items/rock.png", to: 203, toName: "Razz Berry", toAmount: 1, toImage: "/items/razzberry.png" },
    { from: 203, fromName: "Razz Berry", fromAmount: 25, fromImage: "/items/razzberry.png", to: 199, toName: "Rawst Berry", toAmount: 1, toImage: "/items/rawstberry.png" },
    { from: 203, fromName: "Razz Berry", fromAmount: 25, fromImage: "/items/razzberry.png", to: 201, toName: "Aspear Berry", toAmount: 1, toImage: "/items/aspearberry.png" },
    { from: 199, fromName: "Rawst Berry", fromAmount: 10, fromImage: "/items/rawstberry.png", to: 200, toName: "Sitrus Berry", toAmount: 1, toImage: "/items/sitrusberry.png" },
    { from: 201, fromName: "Aspear Berry", fromAmount: 10, fromImage: "/items/aspearberry.png", to: 200, toName: "Sitrus Berry", toAmount: 1, toImage: "/items/sitrusberry.png" },
    { from: 200, fromName: "Sitrus Berry", fromAmount: 10, fromImage: "/items/sitrusberry.png", to: 202, toName: "Magost Berry", toAmount: 1, toImage: "/items/magostberry.png" },
  ];

  // Pokemon change options organized by berry type
  const pokemonChangeOptions = {
    razz: { berryId: 203, berryName: "Razz Berry", berryImage: "/items/razzberry.png", pokemon: [
      { name: "Raticate", cost: 10 },
      { name: "Watchog", cost: 25 },
      { name: "Furret", cost: 25 },
      { name: "Dunsparce", cost: 35 },
      { name: "Miltank", cost: 35 },
      { name: "Tauros", cost: 35 },
      { name: "Cinccino", cost: 35 },
      { name: "Chansey", cost: 50 },
      { name: "Aipom", cost: 50 },
      { name: "Porygon", cost: 50 },
    ]},
    rawst: { berryId: 199, berryName: "Rawst Berry", berryImage: "/items/rawstberry.png", pokemon: [
      { name: "Slugma", cost: 10 },
      { name: "Numel", cost: 10 },
      { name: "Ponyta", cost: 15 },
      { name: "Torkoal", cost: 20 },
      { name: "Growlithe", cost: 35 },
      { name: "Vulpix", cost: 35 },
      { name: "Flareon", cost: 50 },
      { name: "Ninetales", cost: 50 },
      { name: "Tepig", cost: 100 },
      { name: "Cyndaquil", cost: 100 },
    ]},
    aspear: { berryId: 201, berryName: "Aspear Berry", berryImage: "/items/aspearberry.png", pokemon: [
      { name: "Staryu", cost: 10 },
      { name: "Seel", cost: 10 },
      { name: "Krabby", cost: 10 },
      { name: "Pelipper", cost: 15 },
      { name: "Buizel", cost: 15 },
      { name: "Poliwrath", cost: 25 },
      { name: "Kingler", cost: 25 },
      { name: "Floatzel", cost: 25 },
      { name: "Lapras", cost: 35 },
      { name: "Vaporeon", cost: 50 },
      { name: "Oshawott", cost: 100 },
      { name: "Totodile", cost: 100 },
    ]},
    sitrus: { berryId: 200, berryName: "Sitrus Berry", berryImage: "/items/sitrusberry.png", pokemon: [
      { name: "Oddish", cost: 10 },
      { name: "Weedle", cost: 10 },
      { name: "Caterpie", cost: 10 },
      { name: "Cacnea", cost: 15 },
      { name: "Budew", cost: 15 },
      { name: "Tropius", cost: 25 },
      { name: "Exeggutor", cost: 25 },
      { name: "Parasect", cost: 25 },
      { name: "Snivy", cost: 100 },
      { name: "Chikorita", cost: 100 },
    ]},
  };

  const handlePokemonChange = async (slotIndex, newPokemon, cost, berryId) => {
    const hasEnough = getItemAmount(berryId) >= cost;
    
    if (!hasEnough) {
      setErrorMessage(`You need ${cost} ${pokemonChangeOptions.razz.berryName} to change to ${newPokemon}!`);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(
        "/v1/island/change-pokemon",
        {
          slot_index: slotIndex,
          new_pokemon: newPokemon,
          cost_item_id: berryId,
          cost_amount: cost,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        // Immediately update the local state to show the change
        const updatedSpecies = [...pokemonSlotSpecies];
        updatedSpecies[slotIndex] = newPokemon;
        setPokemonSlotSpecies(updatedSpecies);
        
        // Notify parent component to update the Pokemon on the map
        if (onPokemonChange) {
          onPokemonChange(slotIndex, newPokemon);
        }
        
        fetchInventory();
        fetchPokemonSlots();
        setSelectedPokemonSlot(null);
        setErrorMessage(null);
        setSuccessMessage(`${response.data.message} Your Pokemon has been updated on the map!`);
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Pokemon change failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const rockSlotUpgrade = getRockSlotUpgradeCost();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px 10px',
      boxSizing: 'border-box',
      zIndex: 1050
    }} onClick={onClose}>
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '900px',
          maxHeight: 'calc(100vh - 40px)',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/items/razzberry.png"
              alt="Island Shop"
              width={32}
              height={32}
              style={{ imageRendering: 'pixelated' }}
            />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#4CAF50' }}>
              Island Shop
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              color: '#4CAF50',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(76, 175, 80, 0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
          background: 'rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={() => setActiveTab("berries")}
            style={{
              padding: '12px 16px',
              background: activeTab === 'berries' ? 'rgba(76, 175, 80, 0.3)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'berries' ? '2px solid #4CAF50' : 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === 'berries' ? 'bold' : 'normal',
              flex: '1 1 auto',
              minWidth: '80px'
            }}
          >
            Berry Trade
          </button>
          <button
            onClick={() => setActiveTab("rocks")}
            style={{
              padding: '12px 16px',
              background: activeTab === 'rocks' ? 'rgba(76, 175, 80, 0.3)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'rocks' ? '2px solid #4CAF50' : 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === 'rocks' ? 'bold' : 'normal',
              flex: '1 1 auto',
              minWidth: '80px'
            }}
          >
            Rocks
          </button>
          <button
            onClick={() => setActiveTab("pokemon")}
            style={{
              padding: '12px 16px',
              background: activeTab === 'pokemon' ? 'rgba(76, 175, 80, 0.3)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'pokemon' ? '2px solid #4CAF50' : 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === 'pokemon' ? 'bold' : 'normal',
              opacity: 1,
              flex: '1 1 auto',
              minWidth: '80px'
            }}
          >
            Pokemon
          </button>
          <button
            onClick={() => setActiveTab("fishing")}
            style={{
              padding: '12px 16px',
              background: activeTab === 'fishing' ? 'rgba(76, 175, 80, 0.3)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'fishing' ? '2px solid #4CAF50' : 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === 'fishing' ? 'bold' : 'normal',
              flex: '1 1 auto',
              minWidth: '80px'
            }}
          >
            Fishing
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '20px',
          overflowY: 'auto'
        }}>
          {errorMessage && (
            <div style={{
              marginBottom: 16,
              padding: '8px 12px',
              background: 'rgba(255,0,0,0.1)',
              color: '#f87171',
              borderRadius: 4,
              textAlign: 'center',
              fontSize: 14,
            }}>
              {errorMessage}
            </div>
          )}

          {successMessage && (
            <div style={{
              marginBottom: 16,
              padding: '8px 12px',
              background: 'rgba(74, 222, 128, 0.1)',
              color: '#4ade80',
              borderRadius: 4,
              textAlign: 'center',
              fontSize: 14,
              border: '1px solid rgba(74, 222, 128, 0.3)'
            }}>
              {successMessage}
            </div>
          )}

          {activeTab === "berries" && (
            <div>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {berryConversions.map((conversion, index) => {
                  const canAfford = getItemAmount(conversion.from) >= conversion.fromAmount;
                  return (
                    <div
                      key={index}
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        alignItems: 'center'
                      }}
                    >
                      {/* Item Images */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flex: '0 0 auto'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <img
                            src={conversion.fromImage}
                            alt={conversion.fromName}
                            width={48}
                            height={48}
                            style={{ imageRendering: 'pixelated', display: 'block', margin: '0 auto' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                            ×{conversion.fromAmount}
                          </div>
                        </div>
                        <div style={{ color: '#4CAF50', fontSize: '20px', fontWeight: 'bold' }}>→</div>
                        <div style={{ textAlign: 'center' }}>
                          <img
                            src={conversion.toImage}
                            alt={conversion.toName}
                            width={48}
                            height={48}
                            style={{ imageRendering: 'pixelated', display: 'block', margin: '0 auto' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                            ×{conversion.toAmount}
                          </div>
                        </div>
                      </div>

                      {/* Conversion Details */}
                      <div style={{ flex: '1 1 200px', minWidth: '150px' }}>
                        <h5 style={{ color: '#fff', marginBottom: '8px', fontSize: '14px' }}>
                          {conversion.fromAmount} {conversion.fromName} → {conversion.toAmount} {conversion.toName}
                        </h5>
                        <div style={{ fontSize: '12px', color: canAfford ? '#4CAF50' : '#f87171' }}>
                          You have: {getItemAmount(conversion.from)} {conversion.fromName}
                        </div>
                      </div>

                      {/* Convert Button */}
                      <button
                        onClick={() =>
                          handleBerryConversion(
                            conversion.from,
                            conversion.fromAmount,
                            conversion.to,
                            conversion.toAmount
                          )
                        }
                        disabled={loading || !canAfford}
                        style={{
                          padding: '12px 24px',
                          background: canAfford ? '#4CAF50' : '#666',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: canAfford ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          flex: '0 0 auto'
                        }}
                      >
                        Convert
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === "rocks" && (
            <div>
              {/* Tab Selection */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button
                  onClick={() => setSelectedRockSlot(null)}
                  style={{
                    padding: '10px 20px',
                    background: selectedRockSlot === null ? '#4CAF50' : 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: selectedRockSlot === null ? 'bold' : 'normal'
                  }}
                >
                  Unlock Slots
                </button>
                <button
                  onClick={() => setSelectedRockSlot('upgrade')}
                  style={{
                    padding: '10px 20px',
                    background: selectedRockSlot === 'upgrade' ? '#4CAF50' : 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: selectedRockSlot === 'upgrade' ? 'bold' : 'normal'
                  }}
                >
                  Upgrade Rock Type
                </button>
              </div>

              {selectedRockSlot === null && (
                <div>
                  {/* Progress Bar */}
                  <div
                    style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: '1px solid rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    <h4 style={{ color: '#4CAF50', marginBottom: '12px', fontSize: '16px' }}>
                      Rock Slot Progress
                    </h4>
                    <div style={{ color: '#fff', fontSize: '14px', marginBottom: '10px' }}>
                      Current Slots: <strong>{rockSlots} / 18</strong>
                    </div>
                <div
                  style={{
                    background: 'rgba(0, 0, 0, 0.5)',
                    height: '24px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}
                >
                  <div
                    style={{
                      background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                      height: '100%',
                      width: `${(rockSlots / 18) * 100}%`,
                      transition: 'width 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#fff'
                    }}
                  >
                    {Math.round((rockSlots / 18) * 100)}%
                  </div>
                </div>
              </div>

                  {rockSlotUpgrade ? (
                <div
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'center'
                  }}
                >
                  {/* Berry Image */}
                  <div style={{ textAlign: 'center', minWidth: '80px' }}>
                    <img
                      src={rockSlotUpgrade.image}
                      alt={rockSlotUpgrade.itemName}
                      width={64}
                      height={64}
                      style={{ imageRendering: 'pixelated', display: 'block', margin: '0 auto' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                      ×{rockSlotUpgrade.cost}
                    </div>
                  </div>

                  {/* Upgrade Details */}
                  <div style={{ flex: 1 }}>
                    <h4 style={{ color: '#4CAF50', marginBottom: '8px', fontSize: '16px' }}>
                      Unlock Slot #{rockSlotUpgrade.slot}
                    </h4>
                    <div style={{ fontSize: '14px', color: '#fff', marginBottom: '4px' }}>
                      Cost: {rockSlotUpgrade.cost} {rockSlotUpgrade.itemName}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: getItemAmount(rockSlotUpgrade.itemId) >= rockSlotUpgrade.cost ? '#4CAF50' : '#f87171' 
                    }}>
                      You have: {getItemAmount(rockSlotUpgrade.itemId)} {rockSlotUpgrade.itemName}
                    </div>
                  </div>

                  {/* Purchase Button */}
                  <button
                    onClick={() =>
                      handleRockSlotUpgrade(rockSlotUpgrade.cost, rockSlotUpgrade.itemId)
                    }
                    disabled={
                      loading || getItemAmount(rockSlotUpgrade.itemId) < rockSlotUpgrade.cost
                    }
                    style={{
                      padding: '12px 24px',
                      background:
                        getItemAmount(rockSlotUpgrade.itemId) >= rockSlotUpgrade.cost
                          ? '#4CAF50'
                          : '#666',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '5px',
                      cursor:
                        getItemAmount(rockSlotUpgrade.itemId) >= rockSlotUpgrade.cost
                          ? 'pointer'
                          : 'not-allowed',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '120px'
                    }}
                  >
                    {loading ? 'Purchasing...' : 'Purchase'}
                  </button>
                </div>
                  ) : (
                    <div
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '40px 20px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '1px solid rgba(76, 175, 80, 0.5)'
                      }}
                    >
                      <div style={{ color: '#4CAF50', fontSize: '20px', fontWeight: 'bold' }}>
                        Maximum Rock Slots Reached!
                      </div>
                      <div style={{ color: '#aaa', fontSize: '14px', marginTop: '8px' }}>
                        You have unlocked all 18 rock slots
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedRockSlot === 'upgrade' && (
                <div>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}>
                    <h4 style={{ color: '#4CAF50', marginBottom: '12px', fontSize: '16px' }}>
                      Rock Type Upgrades
                    </h4>
                    <p style={{ color: '#aaa', fontSize: '12px', margin: 0 }}>
                      Upgrade your basic rocks to special types with better respawn times and rewards!
                    </p>
                  </div>

                  {/* Rock Type Upgrade Options */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Ruby Upgrade */}
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(220, 38, 38, 0.3)'
                    }}>
                      <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <img
                            src="/items/ruby_rock.png"
                            alt="Ruby Rock"
                            width={48}
                            height={48}
                            style={{ imageRendering: 'pixelated' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div style={{ fontSize: '20px', color: '#ef4444' }}>←</div>
                          <img
                            src="/items/magostberry.png"
                            alt="Magost Berry"
                            width={48}
                            height={48}
                            style={{ imageRendering: 'pixelated' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h5 style={{ color: '#ef4444', marginBottom: '8px', fontSize: '16px' }}>
                            Ruby Rock
                          </h5>
                          <div style={{ fontSize: '14px', color: '#fff', marginBottom: '4px' }}>
                            Cost: 1,500 Magost Berries
                          </div>
                          <div style={{ fontSize: '12px', color: '#aaa' }}>
                            Respawn Time: 15 minutes | You have: {getItemAmount(202)} Magost
                          </div>
                        </div>
                      </div>

                      {/* Rock Selection */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ color: '#fff', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                          Select a basic rock slot to upgrade:
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '8px' }}>
                          {rockSlotTypes.map((type, index) => (
                            type === 'rock' && (
                              <button
                                key={index}
                                onClick={() => handleRockTypeUpgrade(index, 'ruby', 1500)}
                                disabled={loading || getItemAmount(202) < 1500}
                                style={{
                                  padding: '8px',
                                  background: getItemAmount(202) >= 1500 ? 'rgba(220, 38, 38, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                                  border: '1px solid rgba(220, 38, 38, 0.5)',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  fontSize: '11px',
                                  cursor: getItemAmount(202) >= 1500 ? 'pointer' : 'not-allowed',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <img
                                  src="/items/regular_rock.png"
                                  alt="Rock"
                                  width={32}
                                  height={32}
                                  style={{ imageRendering: 'pixelated' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                Slot {index + 1}
                              </button>
                            )
                          ))}
                        </div>
                        {rockSlotTypes.filter(t => t === 'rock').length === 0 && (
                          <div style={{ color: '#aaa', fontSize: '12px', marginTop: '8px' }}>
                            No basic rock slots available to upgrade
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sapphire Upgrade */}
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}>
                      <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <img
                            src="/items/sapphire_rock.png"
                            alt="Sapphire Rock"
                            width={48}
                            height={48}
                            style={{ imageRendering: 'pixelated' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div style={{ fontSize: '20px', color: '#3b82f6' }}>←</div>
                          <img
                            src="/items/magostberry.png"
                            alt="Magost Berry"
                            width={48}
                            height={48}
                            style={{ imageRendering: 'pixelated' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h5 style={{ color: '#3b82f6', marginBottom: '8px', fontSize: '16px' }}>
                            Sapphire Rock
                          </h5>
                          <div style={{ fontSize: '14px', color: '#fff', marginBottom: '4px' }}>
                            Cost: 1,500 Magost Berries
                          </div>
                          <div style={{ fontSize: '12px', color: '#aaa' }}>
                            Respawn Time: 15 minutes | You have: {getItemAmount(202)} Magost
                          </div>
                        </div>
                      </div>

                      {/* Rock Selection */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ color: '#fff', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                          Select a basic rock slot to upgrade:
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '8px' }}>
                          {rockSlotTypes.map((type, index) => (
                            type === 'rock' && (
                              <button
                                key={index}
                                onClick={() => handleRockTypeUpgrade(index, 'sapphire', 1500)}
                                disabled={loading || getItemAmount(202) < 1500}
                                style={{
                                  padding: '8px',
                                  background: getItemAmount(202) >= 1500 ? 'rgba(59, 130, 246, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                                  border: '1px solid rgba(59, 130, 246, 0.5)',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  fontSize: '11px',
                                  cursor: getItemAmount(202) >= 1500 ? 'pointer' : 'not-allowed',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <img
                                  src="/items/regular_rock.png"
                                  alt="Rock"
                                  width={32}
                                  height={32}
                                  style={{ imageRendering: 'pixelated' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                Slot {index + 1}
                              </button>
                            )
                          ))}
                        </div>
                        {rockSlotTypes.filter(t => t === 'rock').length === 0 && (
                          <div style={{ color: '#aaa', fontSize: '12px', marginTop: '8px' }}>
                            No basic rock slots available to upgrade
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Emerald Upgrade */}
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(16, 185, 129, 0.3)'
                    }}>
                      <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <img
                            src="/items/emerald_rock.png"
                            alt="Emerald Rock"
                            width={48}
                            height={48}
                            style={{ imageRendering: 'pixelated' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div style={{ fontSize: '20px', color: '#10b981' }}>←</div>
                          <img
                            src="/items/magostberry.png"
                            alt="Magost Berry"
                            width={48}
                            height={48}
                            style={{ imageRendering: 'pixelated' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h5 style={{ color: '#10b981', marginBottom: '8px', fontSize: '16px' }}>
                            Emerald Rock
                          </h5>
                          <div style={{ fontSize: '14px', color: '#fff', marginBottom: '4px' }}>
                            Cost: 5,000 Magost Berries
                          </div>
                          <div style={{ fontSize: '12px', color: '#aaa' }}>
                            Respawn Time: 30 minutes | You have: {getItemAmount(202)} Magost
                          </div>
                        </div>
                      </div>

                      {/* Rock Selection */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ color: '#fff', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                          Select a basic rock slot to upgrade:
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '8px' }}>
                          {rockSlotTypes.map((type, index) => (
                            type === 'rock' && (
                              <button
                                key={index}
                                onClick={() => handleRockTypeUpgrade(index, 'emerald', 5000)}
                                disabled={loading || getItemAmount(202) < 5000}
                                style={{
                                  padding: '8px',
                                  background: getItemAmount(202) >= 5000 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                                  border: '1px solid rgba(16, 185, 129, 0.5)',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  fontSize: '11px',
                                  cursor: getItemAmount(202) >= 5000 ? 'pointer' : 'not-allowed',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <img
                                  src="/items/regular_rock.png"
                                  alt="Rock"
                                  width={32}
                                  height={32}
                                  style={{ imageRendering: 'pixelated' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                Slot {index + 1}
                              </button>
                            )
                          ))}
                        </div>
                        {rockSlotTypes.filter(t => t === 'rock').length === 0 && (
                          <div style={{ color: '#aaa', fontSize: '12px', marginTop: '8px' }}>
                            No basic rock slots available to upgrade
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "pokemon" && (
            <div>
              {/* Sub-tabs for Pokemon section */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <button
                  onClick={() => setPokemonSubTab('change')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: pokemonSubTab === 'change' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    border: pokemonSubTab === 'change' ? '1px solid rgba(76, 175, 80, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: pokemonSubTab === 'change' ? 'bold' : 'normal'
                  }}
                >
                  Change Pokemon
                </button>
                <button
                  onClick={() => setPokemonSubTab('unlock')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: pokemonSubTab === 'unlock' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    border: pokemonSubTab === 'unlock' ? '1px solid rgba(76, 175, 80, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: pokemonSubTab === 'unlock' ? 'bold' : 'normal'
                  }}
                >
                  Unlock Slots
                </button>
              </div>

              {/* Change Pokemon Tab */}
              {pokemonSubTab === 'change' && (
                <div>
                  <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '8px' }}>
                    <h4 style={{ color: '#4CAF50', marginBottom: '12px', fontSize: '16px' }}>
                      Change Pokemon Species
                    </h4>
                    <p style={{ color: '#aaa', fontSize: '12px', margin: 0 }}>
                      Select a slot and choose a new Pokemon to spawn there!
                    </p>
                  </div>

                  {/* Current Pokemon Slots */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ color: '#fff', fontSize: '14px', display: 'block', marginBottom: '12px' }}>
                      Your Pokemon Slots:
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '12px' }}>
                      {pokemonSlotSpecies.map((species, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedPokemonSlot(index)}
                          style={{
                            padding: '12px',
                            background: selectedPokemonSlot === index ? 'rgba(76, 175, 80, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                            border: selectedPokemonSlot === index ? '2px solid rgba(76, 175, 80, 0.8)' : '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '8px',
                            color: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <img
                            src={`/sprites/${species}.png`}
                            alt={species}
                            width={64}
                            height={64}
                            style={{ imageRendering: 'pixelated' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div style={{ fontSize: '11px', fontWeight: 'bold' }}>Slot {index + 1}</div>
                          <div style={{ fontSize: '10px', color: '#aaa' }}>{species}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Pokemon Selection by Berry Type */}
                  {selectedPokemonSlot !== null && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {/* Razz Berry Pokemon */}
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 105, 180, 0.3)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <img
                            src={pokemonChangeOptions.razz.berryImage}
                            alt={pokemonChangeOptions.razz.berryName}
                            width={32}
                            height={32}
                            style={{ imageRendering: 'pixelated' }}
                          />
                          <h5 style={{ color: '#ff69b4', margin: 0, fontSize: '14px' }}>
                            {pokemonChangeOptions.razz.berryName} Pokemon
                          </h5>
                          <span style={{ color: '#aaa', fontSize: '11px', marginLeft: 'auto' }}>
                            You have: {getItemAmount(pokemonChangeOptions.razz.berryId)}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px' }}>
                          {pokemonChangeOptions.razz.pokemon.map((poke, idx) => (
                            <button
                              key={idx}
                              onClick={() => handlePokemonChange(selectedPokemonSlot, poke.name, poke.cost, pokemonChangeOptions.razz.berryId)}
                              disabled={loading || getItemAmount(pokemonChangeOptions.razz.berryId) < poke.cost}
                              style={{
                                padding: '8px',
                                background: getItemAmount(pokemonChangeOptions.razz.berryId) >= poke.cost ? 'rgba(255, 105, 180, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                                border: '1px solid rgba(255, 105, 180, 0.4)',
                                borderRadius: '6px',
                                color: '#fff',
                                cursor: getItemAmount(pokemonChangeOptions.razz.berryId) >= poke.cost ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <img
                                src={`/sprites/${poke.name}.png`}
                                alt={poke.name}
                                width={48}
                                height={48}
                                style={{ imageRendering: 'pixelated' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div style={{ fontSize: '10px' }}>{poke.name}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                                <img
                                  src="/items/razzberry.png"
                                  alt="Razz Berry"
                                  width={16}
                                  height={16}
                                  style={{ imageRendering: 'pixelated' }}
                                />
                                {poke.cost}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Rawst Berry Pokemon (Fire) */}
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 99, 71, 0.3)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <img
                            src={pokemonChangeOptions.rawst.berryImage}
                            alt={pokemonChangeOptions.rawst.berryName}
                            width={32}
                            height={32}
                            style={{ imageRendering: 'pixelated' }}
                          />
                          <h5 style={{ color: '#ff6347', margin: 0, fontSize: '14px' }}>
                            {pokemonChangeOptions.rawst.berryName} Pokemon (Fire)
                          </h5>
                          <span style={{ color: '#aaa', fontSize: '11px', marginLeft: 'auto' }}>
                            You have: {getItemAmount(pokemonChangeOptions.rawst.berryId)}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px' }}>
                          {pokemonChangeOptions.rawst.pokemon.map((poke, idx) => (
                            <button
                              key={idx}
                              onClick={() => handlePokemonChange(selectedPokemonSlot, poke.name, poke.cost, pokemonChangeOptions.rawst.berryId)}
                              disabled={loading || getItemAmount(pokemonChangeOptions.rawst.berryId) < poke.cost}
                              style={{
                                padding: '8px',
                                background: getItemAmount(pokemonChangeOptions.rawst.berryId) >= poke.cost ? 'rgba(255, 99, 71, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                                border: '1px solid rgba(255, 99, 71, 0.4)',
                                borderRadius: '6px',
                                color: '#fff',
                                cursor: getItemAmount(pokemonChangeOptions.rawst.berryId) >= poke.cost ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <img
                                src={`/sprites/${poke.name}.png`}
                                alt={poke.name}
                                width={48}
                                height={48}
                                style={{ imageRendering: 'pixelated' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div style={{ fontSize: '10px' }}>{poke.name}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                                <img
                                  src="/items/rawstberry.png"
                                  alt="Rawst Berry"
                                  width={16}
                                  height={16}
                                  style={{ imageRendering: 'pixelated' }}
                                />
                                {poke.cost}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Aspear Berry Pokemon (Water) */}
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(64, 164, 223, 0.3)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <img
                            src={pokemonChangeOptions.aspear.berryImage}
                            alt={pokemonChangeOptions.aspear.berryName}
                            width={32}
                            height={32}
                            style={{ imageRendering: 'pixelated' }}
                          />
                          <h5 style={{ color: '#40a4df', margin: 0, fontSize: '14px' }}>
                            {pokemonChangeOptions.aspear.berryName} Pokemon (Water)
                          </h5>
                          <span style={{ color: '#aaa', fontSize: '11px', marginLeft: 'auto' }}>
                            You have: {getItemAmount(pokemonChangeOptions.aspear.berryId)}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px' }}>
                          {pokemonChangeOptions.aspear.pokemon.map((poke, idx) => (
                            <button
                              key={idx}
                              onClick={() => handlePokemonChange(selectedPokemonSlot, poke.name, poke.cost, pokemonChangeOptions.aspear.berryId)}
                              disabled={loading || getItemAmount(pokemonChangeOptions.aspear.berryId) < poke.cost}
                              style={{
                                padding: '8px',
                                background: getItemAmount(pokemonChangeOptions.aspear.berryId) >= poke.cost ? 'rgba(64, 164, 223, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                                border: '1px solid rgba(64, 164, 223, 0.4)',
                                borderRadius: '6px',
                                color: '#fff',
                                cursor: getItemAmount(pokemonChangeOptions.aspear.berryId) >= poke.cost ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <img
                                src={`/sprites/${poke.name}.png`}
                                alt={poke.name}
                                width={48}
                                height={48}
                                style={{ imageRendering: 'pixelated' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div style={{ fontSize: '10px' }}>{poke.name}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                                <img
                                  src="/items/aspearberry.png"
                                  alt="Aspear Berry"
                                  width={16}
                                  height={16}
                                  style={{ imageRendering: 'pixelated' }}
                                />
                                {poke.cost}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Sitrus Berry Pokemon (Grass) */}
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(76, 175, 80, 0.3)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <img
                            src={pokemonChangeOptions.sitrus.berryImage}
                            alt={pokemonChangeOptions.sitrus.berryName}
                            width={32}
                            height={32}
                            style={{ imageRendering: 'pixelated' }}
                          />
                          <h5 style={{ color: '#4caf50', margin: 0, fontSize: '14px' }}>
                            {pokemonChangeOptions.sitrus.berryName} Pokemon (Grass)
                          </h5>
                          <span style={{ color: '#aaa', fontSize: '11px', marginLeft: 'auto' }}>
                            You have: {getItemAmount(pokemonChangeOptions.sitrus.berryId)}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px' }}>
                          {pokemonChangeOptions.sitrus.pokemon.map((poke, idx) => (
                            <button
                              key={idx}
                              onClick={() => handlePokemonChange(selectedPokemonSlot, poke.name, poke.cost, pokemonChangeOptions.sitrus.berryId)}
                              disabled={loading || getItemAmount(pokemonChangeOptions.sitrus.berryId) < poke.cost}
                              style={{
                                padding: '8px',
                                background: getItemAmount(pokemonChangeOptions.sitrus.berryId) >= poke.cost ? 'rgba(76, 175, 80, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                                border: '1px solid rgba(76, 175, 80, 0.4)',
                                borderRadius: '6px',
                                color: '#fff',
                                cursor: getItemAmount(pokemonChangeOptions.sitrus.berryId) >= poke.cost ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <img
                                src={`/sprites/${poke.name}.png`}
                                alt={poke.name}
                                width={48}
                                height={48}
                                style={{ imageRendering: 'pixelated' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div style={{ fontSize: '10px' }}>{poke.name}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                                <img
                                  src="/items/sitrusberry.png"
                                  alt="Sitrus Berry"
                                  width={16}
                                  height={16}
                                  style={{ imageRendering: 'pixelated' }}
                                />
                                {poke.cost}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedPokemonSlot === null && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#aaa', fontSize: '14px' }}>
                      Select a Pokemon slot above to change its species
                    </div>
                  )}
                </div>
              )}

              {/* Unlock Slots Tab */}
              {pokemonSubTab === 'unlock' && (
                <div>
                  {/* Progress Bar */}
                  <div
                    style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: '1px solid rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    <h4 style={{ color: '#4CAF50', marginBottom: '12px', fontSize: '16px' }}>
                      Pokemon Slot Progress
                    </h4>
                    <div style={{ color: '#fff', fontSize: '14px', marginBottom: '10px' }}>
                      Current Slots: <strong>{pokemonSlots} / 12</strong>
                    </div>
                    <div
                      style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        height: '24px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid rgba(76, 175, 80, 0.3)'
                      }}
                    >
                      <div
                        style={{
                          background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                          height: '100%',
                          width: `${(pokemonSlots / 12) * 100}%`,
                          transition: 'width 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#fff'
                        }}
                      >
                        {Math.round((pokemonSlots / 12) * 100)}%
                      </div>
                    </div>
                  </div>

                  {nextPokemonUpgradeCost ? (
                    <div
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        display: 'flex',
                        gap: '20px',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}
                    >
                      {/* Item Image */}
                      <div style={{ textAlign: 'center', minWidth: '80px' }}>
                        <img
                          src={nextPokemonUpgradeCost.item_id === 86 ? "/items/fishercoin.png" : "/items/pearl.png"}
                          alt={nextPokemonUpgradeCost.item_id === 86 ? "Fisher Coin" : "Pearl"}
                          width={64}
                          height={64}
                          style={{ imageRendering: 'pixelated', display: 'block', margin: '0 auto' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                          ×{nextPokemonUpgradeCost.amount}
                        </div>
                      </div>

                      {/* Upgrade Details */}
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <h4 style={{ color: '#4CAF50', marginBottom: '8px', fontSize: '16px' }}>
                          Unlock Slot #{pokemonSlots + 1}
                        </h4>
                        <div style={{ fontSize: '14px', color: '#fff', marginBottom: '4px' }}>
                          Cost: {nextPokemonUpgradeCost.amount} {nextPokemonUpgradeCost.item_id === 86 ? "Fisher Coins" : "Pearls"}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: getItemAmount(nextPokemonUpgradeCost.item_id) >= nextPokemonUpgradeCost.amount ? '#4CAF50' : '#f87171' 
                        }}>
                          You have: {getItemAmount(nextPokemonUpgradeCost.item_id)} {nextPokemonUpgradeCost.item_id === 86 ? "Fisher Coins" : "Pearls"}
                        </div>
                        <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                          New slot will contain: Geodude
                        </div>
                      </div>

                      {/* Purchase Button */}
                      <button
                        onClick={handlePokemonSlotUpgrade}
                        disabled={
                          loading || getItemAmount(nextPokemonUpgradeCost.item_id) < nextPokemonUpgradeCost.amount
                        }
                        style={{
                          padding: '12px 24px',
                          background:
                            getItemAmount(nextPokemonUpgradeCost.item_id) >= nextPokemonUpgradeCost.amount
                              ? '#4CAF50'
                              : '#666',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                          cursor:
                            getItemAmount(nextPokemonUpgradeCost.item_id) >= nextPokemonUpgradeCost.amount
                              ? 'pointer'
                              : 'not-allowed',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          minWidth: '120px'
                        }}
                      >
                        {loading ? 'Purchasing...' : 'Purchase'}
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '40px 20px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '1px solid rgba(76, 175, 80, 0.5)'
                      }}
                    >
  
                      <div style={{ color: '#4CAF50', fontSize: '20px', fontWeight: 'bold' }}>
                        Maximum Pokemon Slots Reached!
                      </div>
                      <div style={{ color: '#aaa', fontSize: '14px', marginTop: '8px' }}>
                        You have unlocked all 12 Pokemon slots
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Fishing Tab */}
          {activeTab === "fishing" && (
            <div>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Old Rod Repair */}
                <div
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    alignItems: 'center'
                  }}
                >
                  {/* Rod Image */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: '0 0 auto'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src="/items/oldrod.png"
                        alt="Old Rod"
                        width={48}
                        height={48}
                        style={{ imageRendering: 'pixelated', display: 'block', margin: '0 auto' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                        Old Rod
                      </div>
                    </div>
                  </div>

                  {/* Repair/Buy Details */}
                  <div style={{ flex: '1 1 200px', minWidth: '150px' }}>
                    {(() => {
                      const oldRod = getOldRod();
                      
                      if (!oldRod) {
                        return (
                          <>
                            <h5 style={{ color: '#fff', marginBottom: '8px', fontSize: '14px' }}>
                              Buy Old Rod
                            </h5>
                            <div style={{ fontSize: '12px', color: '#fbbf24', marginBottom: '4px' }}>
                              You don't have an Old Rod!
                            </div>
                            <div style={{ fontSize: '12px', color: '#aaa' }}>
                              Cost: 500 Coins
                            </div>
                          </>
                        );
                      }
                      
                      return (
                        <>
                          <h5 style={{ color: '#fff', marginBottom: '8px', fontSize: '14px' }}>
                            Repair Old Rod
                          </h5>
                          {oldRod.durability > 0 ? (
                            <div style={{ fontSize: '12px', color: '#4CAF50' }}>
                              Your Old Rod is in good condition! (Durability: {oldRod.durability}/50)
                            </div>
                          ) : (
                            <>
                              <div style={{ fontSize: '12px', color: '#f87171', marginBottom: '4px' }}>
                                Your Old Rod is broken! (Durability: 0/50)
                              </div>
                              <div style={{ fontSize: '12px', color: '#aaa' }}>
                                Cost: 250 Coins
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Buy/Repair Button */}
                  {(() => {
                    const oldRod = getOldRod();
                    
                    if (!oldRod) {
                      return (
                        <button
                          onClick={handleBuyOldRod}
                          disabled={loading}
                          style={{
                            padding: '12px 24px',
                            background: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            flex: '0 0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          Buy (500 
                          <img 
                            src="/items/coin.png" 
                            alt="Coins" 
                            width={16} 
                            height={16} 
                            style={{ imageRendering: 'pixelated' }}
                          />)
                        </button>
                      );
                    }
                    
                    return (
                      <button
                        onClick={handleRepairOldRod}
                        disabled={loading || oldRod.durability > 0}
                        style={{
                          padding: '12px 24px',
                          background: oldRod.durability === 0 ? '#4CAF50' : '#666',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: oldRod.durability === 0 ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          flex: '0 0 auto',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        Repair (250 
                        <img 
                          src="/items/coin.png" 
                          alt="Coins" 
                          width={16} 
                          height={16} 
                          style={{ imageRendering: 'pixelated' }}
                        />)
                      </button>
                    );
                  })()}
                </div>

                {/* Good Rod */}
                <div
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    alignItems: 'center'
                  }}
                >
                  {/* Rod Image */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: '0 0 auto'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src="/items/goodrod.png"
                        alt="Good Rod"
                        width={48}
                        height={48}
                        style={{ imageRendering: 'pixelated', display: 'block', margin: '0 auto' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                        Good Rod
                      </div>
                    </div>
                  </div>

                  {/* Repair/Buy Details */}
                  <div style={{ flex: '1 1 200px', minWidth: '150px' }}>
                    {(() => {
                      const goodRod = getGoodRod();
                      const fisherCoins = getItemAmount(86);
                      
                      if (!goodRod) {
                        return (
                          <>
                            <h5 style={{ color: '#fff', marginBottom: '8px', fontSize: '14px' }}>
                              Buy Good Rod
                            </h5>
                            <div style={{ fontSize: '12px', color: userLevel < 50 ? '#f87171' : '#4CAF50', marginBottom: '4px' }}>
                              {userLevel < 50 ? (
                                <>🔒 Requires Level 50 (You are level {userLevel})</>
                              ) : (
                                <>✓ Level {userLevel} (Requires Level 50)</>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: fisherCoins >= 2500 ? '#4CAF50' : '#f87171' }}>
                              Cost: 2,500 Fisher Coins (You have: {fisherCoins.toLocaleString()})
                            </div>
                          </>
                        );
                      }
                      
                      return (
                        <>
                          <h5 style={{ color: '#fff', marginBottom: '8px', fontSize: '14px' }}>
                            Repair Good Rod
                          </h5>
                          {goodRod.durability > 0 ? (
                            <div style={{ fontSize: '12px', color: '#4CAF50' }}>
                              Your Good Rod is in good condition! (Durability: {goodRod.durability}/100)
                            </div>
                          ) : (
                            <>
                              <div style={{ fontSize: '12px', color: '#f87171', marginBottom: '4px' }}>
                                Your Good Rod is broken! (Durability: 0/100)
                              </div>
                              <div style={{ fontSize: '12px', color: '#aaa' }}>
                                Cost: 1,000 Fisher Coins (You have: {fisherCoins})
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Buy/Repair Button */}
                  {(() => {
                    const goodRod = getGoodRod();
                    const fisherCoins = getItemAmount(86);
                    
                    if (!goodRod) {
                      const canBuy = userLevel >= 50 && fisherCoins >= 2500;
                      return (
                        <button
                          onClick={handleBuyGoodRod}
                          disabled={loading || !canBuy}
                          style={{
                            padding: '12px 24px',
                            background: canBuy ? '#3b82f6' : '#666',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: canBuy ? 'pointer' : 'not-allowed',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            flex: '0 0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          Buy (2,500 
                          <img 
                            src="/items/fishercoin.png" 
                            alt="Fisher Coins" 
                            width={16} 
                            height={16} 
                            style={{ imageRendering: 'pixelated' }}
                          />)
                        </button>
                      );
                    }
                    
                    const canRepair = goodRod.durability === 0 && fisherCoins >= 1000;
                    return (
                      <button
                        onClick={handleRepairGoodRod}
                        disabled={loading || !canRepair}
                        style={{
                          padding: '12px 24px',
                          background: canRepair ? '#4CAF50' : '#666',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: canRepair ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          flex: '0 0 auto',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        Repair (1,000 
                        <img 
                          src="/items/fishercoin.png" 
                          alt="Fisher Coins" 
                          width={16} 
                          height={16} 
                          style={{ imageRendering: 'pixelated' }}
                        />)
                      </button>
                    );
                  })()}
                </div>

                {/* Super Rod */}
                <div
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(168, 85, 247, 0.3)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    alignItems: 'center'
                  }}
                >
                  {/* Rod Image */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: '0 0 auto'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src="/items/superrod.png"
                        alt="Super Rod"
                        width={48}
                        height={48}
                        style={{ imageRendering: 'pixelated', display: 'block', margin: '0 auto' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                        Super Rod
                      </div>
                    </div>
                  </div>

                  {/* Repair/Buy Details */}
                  <div style={{ flex: '1 1 200px', minWidth: '150px' }}>
                    {(() => {
                      const superRod = getSuperRod();
                      const fisherCoins = getItemAmount(86);
                      
                      if (!superRod) {
                        return (
                          <>
                            <h5 style={{ color: '#fff', marginBottom: '8px', fontSize: '14px' }}>
                              Buy Super Rod
                            </h5>
                            <div style={{ fontSize: '12px', color: userLevel < 150 ? '#f87171' : '#4CAF50', marginBottom: '4px' }}>
                              {userLevel < 150 ? (
                                <>🔒 Requires Level 150 (You are level {userLevel})</>
                              ) : (
                                <>✓ Level {userLevel} (Requires Level 150)</>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: fisherCoins >= 5000 ? '#4CAF50' : '#f87171' }}>
                              Cost: 5,000 Fisher Coins (You have: {fisherCoins.toLocaleString()})
                            </div>
                          </>
                        );
                      }
                      
                      return (
                        <>
                          <h5 style={{ color: '#fff', marginBottom: '8px', fontSize: '14px' }}>
                            Repair Super Rod
                          </h5>
                          {superRod.durability > 0 ? (
                            <div style={{ fontSize: '12px', color: '#4CAF50' }}>
                              Your Super Rod is in good condition! (Durability: {superRod.durability}/250)
                            </div>
                          ) : (
                            <>
                              <div style={{ fontSize: '12px', color: '#f87171', marginBottom: '4px' }}>
                                Your Super Rod is broken! (Durability: 0/250)
                              </div>
                              <div style={{ fontSize: '12px', color: '#aaa' }}>
                                Cost: 2,000 Fisher Coins (You have: {fisherCoins})
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Buy/Repair Button */}
                  {(() => {
                    const superRod = getSuperRod();
                    const fisherCoins = getItemAmount(86);
                    
                    if (!superRod) {
                      const canBuy = userLevel >= 150 && fisherCoins >= 5000;
                      return (
                        <button
                          onClick={handleBuySuperRod}
                          disabled={loading || !canBuy}
                          style={{
                            padding: '12px 24px',
                            background: canBuy ? '#a855f7' : '#666',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: canBuy ? 'pointer' : 'not-allowed',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            flex: '0 0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          Buy (5,000 
                          <img 
                            src="/items/fishercoin.png" 
                            alt="Fisher Coins" 
                            width={16} 
                            height={16} 
                            style={{ imageRendering: 'pixelated' }}
                          />)
                        </button>
                      );
                    }
                    
                    const canRepair = superRod.durability === 0 && fisherCoins >= 2000;
                    return (
                      <button
                        onClick={handleRepairSuperRod}
                        disabled={loading || !canRepair}
                        style={{
                          padding: '12px 24px',
                          background: canRepair ? '#4CAF50' : '#666',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: canRepair ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          flex: '0 0 auto',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        Repair (2,000 
                        <img 
                          src="/items/fishercoin.png" 
                          alt="Fisher Coins" 
                          width={16} 
                          height={16} 
                          style={{ imageRendering: 'pixelated' }}
                        />)
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IslandShopModal;
